import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { session } from "../lib/session";
import { getWorkspace } from "../lib/workspaceStore";
import { MetaAnalyticsConnection } from "../components/MetaAnalyticsConnection";

export function connectionStorageKey(email, workspaceId) {
  if (!email || !workspaceId) return null;
  return `omnimark_connections_v2:${encodeURIComponent(email.trim().toLowerCase())}:${encodeURIComponent(workspaceId)}`;
}

export function cachedConnectionNotes(records) {
  return Object.fromEntries(Object.entries(scrubConnectionSecrets(records)).map(([name, record]) => [
    name,
    ["verified", "server_connected", "connected_read_only"].includes(record.status)
      ? { ...record, status: "not_started", last_test: "Connection status must be verified on the server for this account and workspace.", updated_at: null }
      : record,
  ]));
}

export function checkedServerConnection(response) {
  const connection = response?.data?.connection;
  return /^connected(?:_|$)/.test(connection?.status || "") ? connection : null;
}
const shopifyReturnMessages = {
  connected: "Shopify connected securely for read-only product and inventory insights. Installation does not approve a paid plan.",
  cancelled: "Shopify sign-in was cancelled. No provider token was saved.",
  failed: "Shopify could not be connected. No usable provider connection was created.",
  storage_failed: "Shopify verification completed, but saving the connection failed. The connection is not ready.",
  failed_token: "Shopify token exchange failed. No usable connection was saved. OmniMark must check its protected server configuration.",
  failed_shop: "Shopify returned a token, but store verification was rejected. No usable connection was saved. OmniMark must inspect the provider error; adding permissions is not yet justified.",
};

const groups = [
  {
    title: "Dropshipping suppliers",
    desc: "Import product URLs, supplier data, shipping terms, reviews, and product media.",
    route: "/app/dropshipping",
    items: ["Alibaba", "AliExpress", "Temu", "CJdropshipping", "DSers", "Zendrop", "Spocket", "AutoDS", "SaleHoo", "Wholesale2B", "Modalyst", "Printful", "Printify", "Amazon", "Walmart", "eBay", "Etsy"],
  },
  {
    title: "Stores and sites",
    desc: "Connect storefronts so AI can improve pages, offers, products, and customer chat.",
    route: "/app/website",
    items: ["Shopify", "WooCommerce", "Wix", "Squarespace", "BigCommerce", "Webflow", "WordPress", "Custom domain", "OmniMark Website Builder"],
  },
  {
    title: "Ads and social",
    desc: "Plan campaigns and prepare provider setup. Publishing and live control stay disabled until secure OAuth verification and the provider worker are enabled.",
    route: "/app/campaigns",
    items: ["Meta + Instagram", "Facebook Page", "TikTok", "Google Ads", "YouTube", "Pinterest", "Snapchat", "LinkedIn", "X Ads", "Reddit Ads", "Microsoft Ads"],
  },
  {
    title: "Data, chat, and notifications",
    desc: "Power reporting, customer chat, opt-in notifications, and AI memory.",
    route: "/app/library",
    items: ["Google Analytics", "Meta Pixel", "TikTok Pixel", "Stripe", "Supabase", "Klaviyo", "Mailchimp", "Twilio", "SendGrid", "WhatsApp", "Messenger", "Intercom"],
  },
  {
    title: "Developer publishing",
    desc: "Customer-owned source code and store accounts. Repository access, builds, store submissions, and releases always require a separate approval.",
    route: "/app/builder",
    items: ["GitHub", "Apple App Store", "Google Play", "Microsoft Store"],
  },
];

const connectorDetails = {
  Shopify: {
    order: "1",
    status: "Read-only catalog connection",
    detail: "Securely connect one Shopify store so OmniMark can read products, prices, images, vendors, collections, and inventory. No store changes or order access are requested.",
    fields: ["Store URL"],
    unlocks: ["Product scanner", "Website builder", "AI store chat", "Offer testing"],
    actionRoute: "/app/dropshipping",
  },
  CJdropshipping: {
    order: "2",
    status: "Supplier source",
    detail: "Pull supplier products, variants, cost, shipping info, stock risk, and fulfillment choices.",
    fields: ["API key", "API secret", "Warehouse region"],
    unlocks: ["Winning products", "Margin scoring", "Shipping checks", "Supplier auditing"],
    actionRoute: "/app/dropshipping",
  },
  "Meta Marketing API": {
    order: "3",
    label: "Meta + Instagram",
    status: "Ads, pages, and social",
    detail: "Connect Meta first for Facebook pages, ad accounts, spend, reach, CTR, CPA, ROAS, and video performance. Add Instagram inside this same connection after the app has the approved Instagram permissions.",
    fields: ["App ID", "Ad account ID", "Access token", "Facebook page ID/name", "Instagram business account ID", "Instagram review status"],
    unlocks: ["Pause alerts", "Winning videos", "Budget moves", "Instagram stage"],
    actionRoute: "/app/campaigns",
  },
  "TikTok Business API": {
    order: "4",
    status: "TikTok Login Kit",
    detail: "Connect a TikTok account with Login Kit. OmniMark exchanges and encrypts the token on the protected server; posting stays disabled until TikTok approves Content Posting and OmniMark enables a publishing worker.",
    fields: ["TikTok app ID", "Advertiser ID", "Content Posting review status"],
    unlocks: ["Connected account review", "UGC planning", "Hook analysis", "Publishing approval queue"],
    actionRoute: "/app/studio",
  },
  "Google Ads": {
    status: "Search and analytics",
    detail: "Pull Google Ads and Analytics signals for search demand, conversion paths, and paid traffic quality.",
    fields: ["OAuth client ID", "Google Ads customer ID", "Access token"],
    unlocks: ["Search ads", "Analytics", "Conversion signals"],
    actionRoute: "/app/campaigns",
  },
  YouTube: {
    status: "Private-upload channel connection",
    detail: "Connect a YouTube channel with Google OAuth. OmniMark stores the token encrypted; an approved OmniMark request can be uploaded as Private only. Public posting and scheduling remain disabled.",
    fields: ["OAuth client ID", "YouTube channel ID", "OAuth approval status"],
    unlocks: ["Video publishing queue", "Channel analytics", "Creative learning"],
    actionRoute: "/app/campaigns",
  },
  LinkedIn: {
    status: "Organization publishing and ads",
    detail: "Connect the organization owner first. Publishing and marketing reporting require LinkedIn product approval as well as member consent.",
    fields: ["OAuth client ID", "Organization ID", "Marketing API approval status"],
    unlocks: ["Organization posts", "Ad reporting", "B2B creative testing"],
    actionRoute: "/app/campaigns",
  },
  Pinterest: {
    status: "Pins, boards, and ads",
    detail: "Connect a Pinterest business account for boards, Pins, and approved ad reporting.",
    fields: ["App ID", "Business account ID", "OAuth approval status"],
    unlocks: ["Pin publishing queue", "Board planning", "Creative reporting"],
    actionRoute: "/app/campaigns",
  },
  "X Ads": {
    status: "Posts and campaign signals",
    detail: "X uses user-context OAuth with app-level permissions. Save the app status here before enabling a publishing workflow.",
    fields: ["OAuth client ID", "X Ads account ID", "App permission status"],
    unlocks: ["Post queue", "Campaign notes", "Creative testing"],
    actionRoute: "/app/campaigns",
  },
  Stripe: {
    status: "Payments and revenue",
    detail: "Connect payment data so OmniMark can connect ads, products, revenue, refunds, and customer value.",
    fields: ["OAuth client ID", "Account ID", "Access token"],
    unlocks: ["Revenue", "Refunds", "Customer value"],
    actionRoute: "/app/dropshipping",
  },
  WooCommerce: {
    status: "Storefront data",
    detail: "Connect WooCommerce products, orders, coupons, and product page performance.",
    fields: ["Store URL", "Consumer key", "Consumer secret"],
    unlocks: ["Product sync", "Order data", "Offer testing"],
    actionRoute: "/app/website",
  },
  GitHub: {
    status: "Customer-owned code connection",
    detail: "Connect a customer-owned public GitHub repository securely. OmniCode can prepare reviewable commits only for a scoped task; it never exposes the token, merges code, or deploys without a separate approval.",
    fields: ["Repository URL", "OAuth app approval status"],
    unlocks: ["Repository readiness", "Code review plan", "Approval-gated pull requests"],
    actionRoute: "/app/builder",
  },
};

const priorityNames = ["Shopify", "CJdropshipping", "Meta Marketing API", "TikTok Business API"];
const metaServerCallbackPath = "/api/connections/meta/callback";
const tiktokServerCallbackPath = "/api/connections/tiktok/callback";
const googleServerCallbackPath = "/api/connections/google/callback";
const shopifyServerCallbackPath = "/api/connections/shopify/callback";
const githubServerCallbackPath = "/api/connections/github/callback";

const oauthProviderAliases = {
  Meta: "Meta Marketing API",
  Facebook: "Meta Marketing API",
  Instagram: "Meta Marketing API",
  "Meta + Instagram": "Meta Marketing API",
  "Facebook Page": "Meta Marketing API",
  "Meta Pixel": "Meta Marketing API",
  TikTok: "TikTok Business API",
  "TikTok Pixel": "TikTok Business API",
  "Google Analytics": "Google Ads",
};

// These are owner-only setup destinations. They do not send a customer through
// OAuth or collect credentials; each provider's live sign-in remains disabled
// until OmniMark has its approved app, server callback, token exchange, and
// encrypted storage in place.
const providerSetupPortals = [
  { name: "Shopify", label: "Shopify", url: "https://dev.shopify.com/dashboard", need: "Create an API-only app version with the OmniMark callback and only read_products/read_inventory scopes." },
  { name: "TikTok Business API", label: "TikTok", url: "https://developers.tiktok.com/", need: "Create an app, add Login Kit and Content Posting if needed, then request the approved scopes." },
  { name: "Google Ads", label: "Google Ads + YouTube", url: "https://console.cloud.google.com/apis/credentials", need: "Create a web OAuth client and enable only the Google/YouTube APIs you need." },
  { name: "YouTube", label: "YouTube", url: "https://console.cloud.google.com/apis/credentials", need: "Use the same Google Cloud OAuth client and enable YouTube Data API." },
  { name: "LinkedIn", label: "LinkedIn", url: "https://www.linkedin.com/developers/apps", need: "Create an organization-owned developer app and request the needed products." },
  { name: "Pinterest", label: "Pinterest", url: "https://developers.pinterest.com/", need: "Create a business developer app and register the future OmniMark API callback." },
  { name: "X Ads", label: "X", url: "https://developer.x.com/en/portal/dashboard", need: "Create a developer app with user-context OAuth and the minimum read/write permissions." },
];

function isSensitiveConnectionField(field) {
  return /(?:token|secret|api key|consumer key)/i.test(field);
}

function normalizeOauthName(name) {
  return oauthProviderAliases[name] || name;
}

function displayNameFor(name) {
  const connector = connectorDetails[normalizeOauthName(name)] || connectorDetails[name];
  return connector?.label || name;
}

function defaultConnection(name) {
  const details = connectorDetails[name] || {};
  return {
    name,
    // A browser-side setup note is never evidence that a provider account is
    // connected. Only a future server-side token exchange and provider test
    // may mark an integration verified.
    status: "not_started",
    notes: "",
    credentials: Object.fromEntries((details.fields || ["Account URL", "API key", "Notes"]).map((field) => [field, ""])),
    updated_at: null,
  };
}

function loadConnections(storageScope) {
  if (!storageScope) return {};
  try {
    const raw = localStorage.getItem(storageScope);
    return raw ? cachedConnectionNotes(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
}

function saveConnections(records, storageScope) {
  if (!storageScope) return;
  // Browser storage is not a credential vault. Keep workflow notes and public
  // identifiers, but never persist provider tokens, secrets, or API keys here.
  localStorage.setItem(storageScope, JSON.stringify(cachedConnectionNotes(records)));
}

function scrubConnectionSecrets(records) {
  return Object.fromEntries(Object.entries(records || {}).map(([name, record]) => {
    const credentials = Object.fromEntries(Object.entries(record?.credentials || {}).map(([field, value]) => [
      field,
      /token|secret|api key/i.test(field) ? "" : value,
    ]));
    return [name, { ...record, credentials }];
  }));
}

function cleanStoreUrl(value) {
  const raw = (value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    return url.hostname;
  } catch {
    return raw.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  }
}

export default function ConnectionsPage() {
  const activeWorkspace = getWorkspace();
  const storageScope = connectionStorageKey(session.getEmail(), activeWorkspace?.id);
  // A scope change remounts the stateful page before it can save old notes in
  // another workspace. Legacy shared notes remain untouched but are not read.
  return <ScopedConnectionsPage key={storageScope || "unselected"} activeWorkspace={activeWorkspace} storageScope={storageScope} />;
}

function ScopedConnectionsPage({ activeWorkspace, storageScope }) {
  const setupPanelRef = useRef(null);
  const workspaceName = activeWorkspace?.name || "";
  const isMyValueWorkspace = /my value of life/i.test(workspaceName);
  const defaultAssistBusiness = isMyValueWorkspace ? "My Value of Life" : workspaceName || "Your business";
  const defaultAssistWebsite = isMyValueWorkspace ? "https://myvalueoflife.com" : "";
  const defaultAssistMetaPage = isMyValueWorkspace ? "My Value of Life" : workspaceName || "";
  const [me, setMe] = useState(null);
  const [records, setRecords] = useState(() => loadConnections(storageScope));
  const [selectedName, setSelectedName] = useState("Shopify");
  const shopifyReturnResult = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("shopify");
    return Object.hasOwn(shopifyReturnMessages, value) ? value : null;
  }, []);
  const [msg, setMsg] = useState(() => shopifyReturnMessages[shopifyReturnResult] || "");
  const [testing, setTesting] = useState(false);
  const [assistBusiness, setAssistBusiness] = useState(defaultAssistBusiness);
  const [assistWebsite, setAssistWebsite] = useState(defaultAssistWebsite);
  const [assistMetaPage, setAssistMetaPage] = useState(defaultAssistMetaPage);

  useEffect(() => {
    if (!shopifyReturnResult) return;
    setSelectedName("Shopify");
    setupPanelRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
    setupPanelRef.current?.focus({ preventScroll: true });
  }, [shopifyReturnResult]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/users/me");
        setMe(res.data);
      } catch {
        setMe(null);
      }
    })();
  }, []);

  useEffect(() => {
    let active = true;
    const workspaceId = activeWorkspace?.id;
    if (!workspaceId) return undefined;
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.get("/api/connections/meta/status", { params: { workspace_id: workspaceId } }),
          api.get("/api/connections/tiktok/status", { params: { workspace_id: workspaceId } }),
          api.get("/api/connections/google/status", { params: { workspace_id: workspaceId } }),
          api.get("/api/connections/shopify/status", { params: { workspace_id: workspaceId } }),
          api.get("/api/connections/github/status", { params: { workspace_id: workspaceId } }),
        ]);
        if (!active) return;
        const [metaConnection, tiktokConnection, googleConnection, shopifyConnection, githubConnection] = results.map(
          (result) => checkedServerConnection(result.status === "fulfilled" ? result.value : null)
        );
        if (results.some((result) => result.status === "rejected") && !shopifyReturnResult) {
          setMsg("Some provider checks are unavailable. Only connections verified by the server for this workspace are shown as connected.");
        }
        if (metaConnection) {
          const pagePublishingReady = (metaConnection.scopes || []).includes("pages_manage_posts");
          setRecords((current) => ({
            ...current,
            "Meta Marketing API": {
              ...(current["Meta Marketing API"] || defaultConnection("Meta Marketing API")),
              status: pagePublishingReady ? "server_connected" : "connected_read_only",
              last_test: pagePublishingReady
                ? "Meta account is connected on OmniMark's protected server. A rights-cleared video can publish only after AI policy review, Admin approval, Page selection, and a final publish confirmation in Clip Studio."
                : "Meta account is connected for approved read and insight paths. Page publishing is blocked until Meta grants the Page-management permission.",
              updated_at: metaConnection.last_verified_at || new Date().toISOString(),
            },
          }));
        }
        if (tiktokConnection) {
          setRecords((current) => ({
            ...current,
            "TikTok Business API": {
              ...(current["TikTok Business API"] || defaultConnection("TikTok Business API")),
              status: "server_connected",
              last_test: "TikTok is connected on OmniMark's protected server. Posting stays disabled until TikTok approves Content Posting and OmniMark enables a publishing worker.",
              updated_at: tiktokConnection.last_verified_at || new Date().toISOString(),
            },
          }));
        }
        if (googleConnection) {
          const privateUploadReady = (googleConnection.scopes || []).includes("https://www.googleapis.com/auth/youtube.upload");
          setRecords((current) => ({
            ...current,
            YouTube: {
              ...(current.YouTube || defaultConnection("YouTube")),
              status: "server_connected",
              last_test: privateUploadReady
                ? "Google/YouTube is connected on OmniMark's protected server. Approved requests may be uploaded as Private videos only; public posting and scheduling remain disabled."
                : "Google/YouTube is connected for a read-only channel check on OmniMark's protected server. Reconnect and approve YouTube upload permission to enable private uploads.",
              updated_at: googleConnection.last_verified_at || new Date().toISOString(),
            },
          }));
        }
        if (shopifyConnection) {
          setRecords((current) => ({
            ...current,
            Shopify: {
              ...(current.Shopify || defaultConnection("Shopify")),
              status: "server_connected",
              last_test: "Shopify is connected on OmniMark's protected server for read-only product and inventory insights. OmniMark cannot edit products, orders, or store settings through this connection.",
              updated_at: shopifyConnection.last_verified_at || new Date().toISOString(),
            },
          }));
        }
        if (githubConnection) {
          setRecords((current) => ({
            ...current,
            GitHub: {
              ...(current.GitHub || defaultConnection("GitHub")),
              status: "server_connected",
              last_test: `GitHub is connected securely${githubConnection.display_name ? ` as ${githubConnection.display_name}` : ""}. ${githubConnection.status === "connected_code_ready" ? "Public repository commit access is available only for approved OmniCode tasks; deploy and merge remain separate approvals." : "Identity-only access is connected; reconnect through the code connection action before OmniCode can save a commit."}`,
              updated_at: githubConnection.last_verified_at || new Date().toISOString(),
            },
          }));
        }
        const result = new URLSearchParams(window.location.search).get("meta");
        if (result === "connected") setMsg("Meta connected securely. OmniMark stored the token encrypted. Rights-cleared videos require AI review, Admin approval, Page selection, and a final confirmation in Clip Studio before publishing.");
        if (result === "cancelled") setMsg("Meta sign-in was cancelled. No provider token was saved.");
        if (result === "failed" || result === "storage_failed") setMsg("Meta could not be connected. No usable provider connection was created.");
        const tiktokResult = new URLSearchParams(window.location.search).get("tiktok");
        if (tiktokResult === "connected") setMsg("TikTok connected securely. Posting remains pending TikTok Content Posting approval and the publishing worker.");
        if (tiktokResult === "cancelled") setMsg("TikTok sign-in was cancelled. No provider token was saved.");
        if (tiktokResult === "failed" || tiktokResult === "storage_failed") setMsg("TikTok could not be connected. No usable provider connection was created.");
        const googleResult = new URLSearchParams(window.location.search).get("google");
        if (googleResult === "connected") setMsg("Google/YouTube connected securely. Approved requests can be sent as Private videos only; public posting and scheduling remain disabled.");
        if (googleResult === "cancelled") setMsg("Google sign-in was cancelled. No provider token was saved.");
        if (googleResult === "failed" || googleResult === "storage_failed") setMsg("Google/YouTube could not be connected. Confirm the YouTube Data API is enabled and that this Google account is a test user.");
        if (googleResult === "failed_token") setMsg("Google approved access, but its token exchange was rejected. OmniMark has not saved a connection; verify the installed OAuth client secret belongs to this Google web client.");
        if (googleResult === "failed_profile") setMsg("Google returned a token, but the protected profile check failed. No connection was saved.");
        if (googleResult === "failed_channel") setMsg("Google returned a token, but the YouTube channel check failed. Confirm YouTube Data API v3 is enabled for this same Google project.");
        const shopifyResult = new URLSearchParams(window.location.search).get("shopify");
        if (Object.hasOwn(shopifyReturnMessages, shopifyResult)) setMsg(shopifyReturnMessages[shopifyResult]);
        const githubResult = new URLSearchParams(window.location.search).get("github");
        if (githubResult === "connected") {
          // The backend emits this return only after its encrypted upsert has
          // succeeded. The status endpoint refreshes it again when the
          // session is available; this keeps the return page truthful while
          // that refresh is still restoring.
          // A URL parameter is not evidence of a connection. The authenticated
          // status result above is the only authority for the connected badge.
          setMsg("GitHub code connection completed securely. OmniCode can save reviewable public-repository commits for approved tasks; merge and deployment remain separate approvals.");
        }
        if (githubResult === "failed" || githubResult === "storage_failed") setMsg("GitHub could not be connected. No usable GitHub connection was created.");
        if (githubResult === "failed_token" || githubResult === "failed_profile") setMsg("GitHub approved access, but OmniMark could not finish the protected token or identity check. No connection was saved.");
      } catch {
        // A missing connection table or unavailable backend must never make the
        // browser claim that Meta is connected.
      }
    })();
    return () => { active = false; };
  }, [activeWorkspace?.id, shopifyReturnResult]);

  useEffect(() => {
    saveConnections(records, storageScope);
  }, [records, storageScope]);

  useEffect(() => {
    const returnMessage = localStorage.getItem("omnimark_connections_return_message");
    if (returnMessage) {
      setMsg(returnMessage);
      localStorage.removeItem("omnimark_connections_return_message");
    }

    setRecords((current) => {
      let changed = false;
      const next = { ...current };
      Object.entries(next).forEach(([name, record]) => {
        if (record?.oauth?.code_received && record.status !== "verified") {
          next[name] = {
            ...record,
            status: "oauth_returned",
            last_test: record.last_test || "Authorization returned from provider. Token exchange and a live API test are still required before this is connected.",
          };
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, []);

  const selected = records[selectedName] || defaultConnection(selectedName);
  const selectedDetails = connectorDetails[selectedName] || {
    status: "Connector-ready",
    detail: "Save account details and setup notes so OmniMark can use this connection when the official API is enabled.",
    fields: Object.keys(selected.credentials || {}).length ? Object.keys(selected.credentials) : ["Account URL", "API key", "Notes"],
    unlocks: ["AI workflows", "Reporting", "Automation"],
    actionRoute: groupRouteFor(selectedName),
  };

  const summary = useMemo(() => {
    // Group labels include customer-facing aliases such as "TikTok" and
    // "Meta + Instagram", while secure OAuth state is stored under the
    // canonical provider name. Normalizing and de-duplicating here keeps the
    // headline count aligned with the cards below it and avoids counting one
    // Meta connection multiple times (Meta, Facebook Page, and Meta Pixel).
    const providerNames = [...new Set(groups
      .flatMap((group) => group.items)
      .map((name) => normalizeOauthName(name)))];
    const known = providerNames.map((name) => ({ name, ...(records[name] || defaultConnection(name)) }));
    const connectedItems = known.filter((item) => ["verified", "server_connected", "connected_read_only"].includes(item.status));
    const setupItems = known.filter((item) => item.status === "setup");
    const needsKeyItems = known.filter((item) => item.status === "needs_keys");
    return {
      // A read-only OAuth connection is still a real, encrypted server-side
      // connection. It should count as connected while the UI continues to
      // make the missing publishing approval explicit.
      connected: connectedItems.length,
      setup: setupItems.length,
      needsKeys: needsKeyItems.length,
      connectedItems,
      setupItems,
      needsKeyItems,
    };
  }, [records]);

  const plan = me?.is_admin ? "admin" : me?.plan || "trial";
  const ownerEmail = session.getEmail();
  const canUseMyValueShortcut = isMyValueWorkspace || session.isOwnerAdmin(ownerEmail);
  const setupTitle = isMyValueWorkspace ? "Connect My Value of Life faster" : "Connect your business faster";
  const setupCopy = isMyValueWorkspace
    ? "OmniAssist can prepare the exact connection notes for your Facebook page, Instagram, website/domain, pixel, and ad account so you are not guessing what to enter."
    : "OmniAssist can prepare connection notes for your Facebook page, Instagram, website/domain, pixel, store, supplier feeds, and ad account so setup feels guided instead of confusing.";
  const metaStep = assistMetaPage
    ? `Use one Meta + Instagram connection for ${assistMetaPage}: Meta permissions first, Instagram permissions after app approval.`
    : "Use one Meta + Instagram connection: Meta permissions first, Instagram permissions after app approval.";
  const metaApiOrigin = (() => {
    try {
      return new URL(api.defaults.baseURL || window.location.origin, window.location.origin).origin;
    } catch {
      return window.location.origin;
    }
  })();
  const metaCallbackUrl = typeof window !== "undefined" ? `${metaApiOrigin}${metaServerCallbackPath}` : "";
  const tiktokCallbackUrl = typeof window !== "undefined" ? `${metaApiOrigin}${tiktokServerCallbackPath}` : "";
  const googleCallbackUrl = typeof window !== "undefined" ? `${metaApiOrigin}${googleServerCallbackPath}` : "";
  const shopifyCallbackUrl = typeof window !== "undefined" ? `${metaApiOrigin}${shopifyServerCallbackPath}` : "";
  const githubCallbackUrl = typeof window !== "undefined" ? `${metaApiOrigin}${githubServerCallbackPath}` : "";
  const selectedIsShopify = selectedName === "Shopify";
  const selectedIsMeta = selectedName === "Meta Marketing API";
  const selectedIsTikTok = selectedName === "TikTok Business API";
  const selectedIsGoogle = selectedName === "YouTube";
  const selectedIsGitHub = selectedName === "GitHub";
  const selectedHasSecureOAuth = selectedIsMeta || selectedIsTikTok || selectedIsGoogle || selectedIsShopify || selectedIsGitHub;
  const selectedProviderLabel = selectedIsMeta
    ? "Meta + Instagram"
    : selectedIsTikTok
      ? "TikTok"
      : selectedIsGoogle
        ? "YouTube"
        : selectedIsShopify
          ? "Shopify"
          : selectedIsGitHub
            ? "GitHub"
        : selectedName;
  const selectedProviderPortal = providerSetupPortals.find((provider) => provider.name === selectedName);
  const selectedHelp = connectionHelpFor(selectedName, selectedIsMeta ? metaCallbackUrl : selectedIsTikTok ? tiktokCallbackUrl : selectedIsGoogle ? googleCallbackUrl : selectedIsShopify ? shopifyCallbackUrl : selectedIsGitHub ? githubCallbackUrl : "");

  function groupRouteFor(name) {
    return groups.find((group) => group.items.includes(name))?.route || "/app";
  }

  function updateConnection(name, updater) {
    setRecords((current) => {
      const existing = current[name] || defaultConnection(name);
      const next = {
        ...current,
        [name]: {
          ...existing,
          ...updater(existing),
          updated_at: new Date().toISOString(),
        },
      };
      return next;
    });
  }

  function startSetup(name) {
    setSelectedName(name);
    updateConnection(name, () => ({ status: "setup" }));
    setMsg(`${name} setup opened. Add public IDs and notes; provider verification is required before OmniMark can use live data or publish.`);
    // The selected setup panel is beside the priority cards on wide screens and
    // below them on narrow ones. Put it in view so a card tap never looks like
    // it did nothing.
    requestAnimationFrame(() => setupPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  function markReadyForVerification(name) {
    updateConnection(name, () => ({ status: "verification_required" }));
    setMsg(`${name} is saved as ready for server-side verification. It is not connected for publishing or live reporting yet.`);
  }

  function markNeedsKeys(name) {
    updateConnection(name, () => ({ status: "needs_keys" }));
    setMsg(`${name} saved as waiting on API keys or OAuth approval.`);
  }

  function updateCredential(field, value) {
    updateConnection(selectedName, (existing) => ({
      credentials: {
        ...existing.credentials,
        [field]: value,
      },
    }));
  }

  function updateNotes(value) {
    updateConnection(selectedName, () => ({ notes: value }));
  }

  async function copySetupBrief() {
    const credentialNames = Object.keys(selected.credentials || {});
    const text = `OmniMark HQ connection setup: ${selectedName}
Status: ${selected.status}
Needed fields: ${credentialNames.join(", ")}
Use for: ${(selectedDetails.unlocks || []).join(", ")}
Notes: ${selected.notes || "None yet"}`;
    try {
      await navigator.clipboard.writeText(text);
      setMsg(`${selectedName} setup brief copied.`);
    } catch {
      setMsg("Could not copy setup brief. Select the setup text manually.");
    }
  }

  async function testConnection() {
    setTesting(true);
    try {
      await api.get("/api/health");
      updateConnection(selectedName, () => ({ last_test: "OmniMark API reachable" }));
      setMsg(`OmniMark API is reachable. This confirms OmniMark is online; it does not verify ${selectedName} or its provider credentials.`);
    } catch {
      setMsg("OmniMark API check failed. Try again after the backend is reachable.");
    } finally {
      setTesting(false);
    }
  }

  async function startProviderSignIn(name = selectedName) {
    const connector = normalizeOauthName(name);
    setSelectedName(connector);
    if (connector === "Shopify") {
      if (!activeWorkspace?.id) {
        setMsg("Choose a workspace before connecting Shopify.");
        return;
      }
      const shop = cleanStoreUrl((records.Shopify || defaultConnection("Shopify")).credentials?.["Store URL"] || "");
      if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shop)) {
        setMsg("Enter the store's exact .myshopify.com domain first, for example store.myshopify.com.");
        return;
      }
      try {
        const response = await api.post("/api/connections/shopify/start", { workspace_id: activeWorkspace.id, shop });
        const authorizationUrl = response.data?.authorization_url;
        if (!authorizationUrl) throw new Error("No Shopify authorization URL was returned.");
        window.location.assign(authorizationUrl);
        return;
      } catch (error) {
        const detail = error?.response?.data?.detail || "Shopify secure OAuth is not ready yet. No provider redirect was started.";
        updateConnection(connector, (existing) => ({ ...existing, status: "verification_required", last_test: detail }));
        setMsg(detail);
        return;
      }
    }
    if (connector === "Meta Marketing API") {
      if (!activeWorkspace?.id) {
        setMsg("Choose a workspace before connecting Meta.");
        return;
      }
      try {
        const response = await api.post("/api/connections/meta/start", { workspace_id: activeWorkspace.id });
        const authorizationUrl = response.data?.authorization_url;
        if (!authorizationUrl) throw new Error("No Meta authorization URL was returned.");
        window.location.assign(authorizationUrl);
        return;
      } catch (error) {
        const detail = error?.response?.data?.detail || "Meta secure OAuth is not ready yet. No provider redirect was started.";
        updateConnection(connector, (existing) => ({ ...existing, status: "verification_required", last_test: detail }));
        setMsg(detail);
        return;
      }
    }
    if (connector === "TikTok Business API") {
      if (!activeWorkspace?.id) {
        setMsg("Choose a workspace before connecting TikTok.");
        return;
      }
      try {
        const response = await api.post("/api/connections/tiktok/start", { workspace_id: activeWorkspace.id });
        const authorizationUrl = response.data?.authorization_url;
        if (!authorizationUrl) throw new Error("No TikTok authorization URL was returned.");
        window.location.assign(authorizationUrl);
        return;
      } catch (error) {
        const detail = error?.response?.data?.detail || "TikTok secure OAuth is not ready yet. No provider redirect was started.";
        updateConnection(connector, (existing) => ({ ...existing, status: "verification_required", last_test: detail }));
        setMsg(detail);
        return;
      }
    }
    if (connector === "YouTube") {
      if (!activeWorkspace?.id) {
        setMsg("Choose a workspace before connecting Google/YouTube.");
        return;
      }
      try {
        const response = await api.post("/api/connections/google/start", { workspace_id: activeWorkspace.id });
        const authorizationUrl = response.data?.authorization_url;
        if (!authorizationUrl) throw new Error("No Google authorization URL was returned.");
        window.location.assign(authorizationUrl);
        return;
      } catch (error) {
        const detail = error?.response?.data?.detail || "Google secure OAuth is not ready yet. No provider redirect was started.";
        updateConnection(connector, (existing) => ({ ...existing, status: "verification_required", last_test: detail }));
        setMsg(detail);
        return;
      }
    }
    if (connector === "GitHub") {
      if (!activeWorkspace?.id) {
        setMsg("Choose a workspace before connecting GitHub.");
        return;
      }
      try {
        const response = await api.post("/api/connections/github/start", { workspace_id: activeWorkspace.id });
        const authorizationUrl = response.data?.authorization_url;
        if (!authorizationUrl) throw new Error("No GitHub authorization URL was returned.");
        window.location.assign(authorizationUrl);
        return;
      } catch (error) {
        const detail = error?.response?.data?.detail || "GitHub secure OAuth is not ready yet. No provider redirect was started.";
        updateConnection(connector, (existing) => ({ ...existing, status: "verification_required", last_test: detail }));
        setMsg(detail);
        return;
      }
    }
    // Do not send a customer through OAuth until the server owns the state,
    // client secret, token exchange, encrypted token vault, and provider test.
    // A returned browser code alone is not a connected account.
    updateConnection(connector, (existing) => ({
      ...existing,
      status: "verification_required",
      last_test: "Secure OAuth is not configured for this provider yet. Add the approved provider app to OmniMark's server configuration; do not paste tokens or secrets here.",
    }));
    setMsg(`${connector} sign-in is held until its server-side OAuth exchange and provider verification are configured. No provider redirect was started.`);
  }

  async function copyCallbackUrl(provider = "Meta") {
    const isTikTok = provider === "TikTok";
    const isGoogle = provider === "Google";
    const isShopify = provider === "Shopify";
    const text = isShopify ? shopifyCallbackUrl : isGoogle ? googleCallbackUrl : isTikTok ? tiktokCallbackUrl : metaCallbackUrl;
    try {
      await navigator.clipboard.writeText(text);
      setMsg(`${isShopify ? "Shopify" : isGoogle ? "Google" : isTikTok ? "TikTok" : "Meta"} API callback URL copied. Add this exact URL only in that provider's app settings.`);
    } catch {
      setMsg(`${isShopify ? "Shopify" : isGoogle ? "Google" : isTikTok ? "TikTok" : "Meta"} API callback URL: ${text}`);
    }
  }

  async function copyProviderFixSteps() {
    const text = `${selectedHelp.title}

Callback URL:
${selectedIsMeta ? metaCallbackUrl : selectedIsTikTok ? tiktokCallbackUrl : selectedIsGoogle ? googleCallbackUrl : selectedIsShopify ? shopifyCallbackUrl : "Not assigned yet — OmniMark will create a provider-specific API callback when this provider's secure server flow is enabled."}

Steps:
${selectedHelp.steps.map((step, index) => `${index + 1}. ${step}`).join("\n")}

Fallback:
${selectedHelp.fallback}`;
    try {
      await navigator.clipboard.writeText(text);
      setMsg(`${selectedName} fix steps copied. Send this to the person who manages the third-party app if needed.`);
    } catch {
      setMsg(selectedHelp.fallback);
    }
  }

  function saveBlockedRedirectNote(name = selectedName) {
    const connector = normalizeOauthName(name);
    setSelectedName(connector);
    updateConnection(connector, (existing) => ({
      status: "needs_keys",
      notes: [
        existing.notes || "",
        `${connector} setup is waiting on its approved provider app and secure OmniMark server flow.${connector === "Meta Marketing API" ? ` Add this callback in Meta: ${metaCallbackUrl}` : " Do not register the old browser callback; OmniMark will provide the provider-specific API callback when enabled."}`,
      ].filter(Boolean).join("\n"),
      last_test: connector === "Meta Marketing API" ? `Provider needs this callback URL whitelisted: ${metaCallbackUrl}` : "Provider app and secure server-side OAuth setup are still required before a callback can be registered.",
    }));
    setMsg(connector === "Meta Marketing API"
      ? `${connector} saved as needs setup. Add the Meta API callback in the provider app, then try sign-in again.`
      : `${connector} saved as needs setup. Open its owner setup portal; OmniMark will enable the one-click customer sign-in after its secure provider flow is configured.`);
  }

  function startMyValueOfLifeMeta() {
    const notes = [
      `Business/page: ${assistBusiness}`,
      `Website/domain: ${assistWebsite}`,
      `Meta page: ${assistMetaPage}`,
      "From Meta Business Suite: confirm Page access, connect the Instagram account to the page, verify business assets, then create or confirm an ad account.",
      "Needed for OmniMark: Meta App ID, Ad Account ID, Page ID/name, access token or OAuth approval, Instagram business account ID/review status, and pixel/dataset ID if available.",
    ].join("\n");
    setSelectedName("Meta Marketing API");
    updateConnection("Meta Marketing API", (existing) => ({
      status: "setup",
      notes,
      credentials: {
        ...existing.credentials,
        "Ad account ID": existing.credentials?.["Ad account ID"] || "",
        "App ID": existing.credentials?.["App ID"] || "",
        "Access token": existing.credentials?.["Access token"] || "",
      },
    }));
    setMsg(`${assistBusiness} Meta setup is ready. Add the App ID, Ad Account ID, and token when you have them.`);
  }

  function startMyValueOfLifeSite() {
    const notes = [
      `Business/site: ${assistBusiness}`,
      `Website/domain: ${assistWebsite}`,
      "Use this connection for website/page building, domain context, customer chat, policies, and landing page improvements.",
      "Next: save domain access notes, connect analytics/pixel, then use Website Builder and Brand DNA.",
    ].join("\n");
    setSelectedName("Custom domain");
    updateConnection("Custom domain", (existing) => ({
      status: "setup",
      notes,
      credentials: {
        ...existing.credentials,
        "Account URL": assistWebsite,
      },
    }));
    setMsg(`${assistBusiness} website setup is ready. Save domain notes and open Website Builder when ready.`);
  }

  async function copyMyValueOfLifeChecklist() {
    const text = `OmniMark HQ connection checklist for ${assistBusiness}
1. In Meta Business Suite, confirm ${assistMetaPage} is selected.
2. Confirm Instagram is connected to that Facebook page inside Meta Business Suite.
3. Open Ads Manager and confirm the ad account for this page.
4. Get or create Meta App ID and Marketing API access.
5. Save Ad Account ID, Page ID/name, Instagram business account ID/review status, and token/OAuth status in OmniMark Connections.
6. Add website/domain: ${assistWebsite}.
7. Add Meta Pixel or dataset ID when available.
8. Open Campaigns so OmniMark can use ad performance signals.`;
    try {
      await navigator.clipboard.writeText(text);
      setMsg(`${assistBusiness} connection checklist copied.`);
    } catch {
      setMsg("Could not copy checklist. Select the checklist text manually.");
    }
  }

  function statusLabel(status) {
    if (status === "verified") return "Verified";
    if (status === "server_connected") return "Connected — publishing pending";
    if (status === "connected_read_only") return "Connected — Page publishing needs approval";
    if (status === "oauth_returned") return "OAuth returned";
    if (status === "verification_required") return "Needs verification";
    if (status === "setup") return "In setup";
    if (status === "needs_keys") return "Needs keys";
    return "Start setup";
  }

  function openConnectionDetails(name) {
    setSelectedName(name);
    requestAnimationFrame(() => setupPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  function connectionInsight(record, connectorName = "") {
    if (record?.oauth?.code_received) {
      return "OAuth returned successfully. Server-side token exchange and a provider API test are still required.";
    }
    if (record?.status === "verified") {
      return "Server verification passed. This provider may be used only for its approved workflows.";
    }
    if (record?.status === "server_connected") {
      if (connectorName === "Meta Marketing API") return "The account is connected with encrypted server-side credentials. Meta Page publishing is approval-gated in Clip Studio; no video can publish without a selected Page and final confirmation.";
      if (connectorName === "TikTok Business API") return "TikTok Login Kit is connected with encrypted server-side credentials. Posting remains blocked until TikTok approves Content Posting and OmniMark enables its dedicated publishing worker.";
      if (connectorName === "YouTube") return "Google/YouTube is connected with encrypted server-side credentials. Only an approved request may upload as Private; public posting and scheduling remain unavailable.";
      return "The account is connected with encrypted server-side credentials. Only the provider workflows explicitly enabled for this connection are available.";
    }
    if (record?.status === "connected_read_only") {
      return "The account is connected with encrypted server-side credentials for approved read and insight paths. Page publishing stays blocked until Meta grants the Page-management permission.";
    }
    if (record?.status === "verification_required") {
      return "Setup details are saved locally. Server-side token exchange and provider verification are still required.";
    }
    if (record?.status === "setup") {
      return "Setup started. Add public account identifiers and complete the provider’s secure server-side requirements.";
    }
    if (record?.status === "needs_keys") {
      return "Waiting on app approval, redirect URL setup, or API credentials.";
    }
    return "Not connected yet.";
  }

  return (
    <div style={page} data-testid="connections-page">
      <section style={hero}>
        <div>
          <div style={eyebrow}>Integration hub</div>
          <h1 style={title}>Connections</h1>
          <p style={subtitle}>
            Prepare connections without getting stuck. OmniMark gives each provider a guided path, secure server-side requirements, and a manual fallback when the outside app needs extra approval.
          </p>
        </div>
        <div style={heroActions}>
          <div style={connectionDirectory}>
            <div>
              <strong>Find a connection</strong>
              <span>Choose a platform, review its status, then start its secure flow only when it is ready.</span>
            </div>
            <div style={directoryActions}>
              <select
                aria-label="Choose a connection"
                value={selectedName}
                onChange={(event) => setSelectedName(event.target.value)}
                style={directorySelect}
              >
                {Object.keys(connectorDetails).map((name) => <option key={name} value={name}>{displayNameFor(name)}</option>)}
              </select>
              <button
                type="button"
                onClick={() => requestAnimationFrame(() => setupPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }))}
                style={darkButton}
              >
                Open setup
              </button>
            </div>
          </div>
        </div>
      </section>

      <div style={summaryGrid}>
        <details style={summaryBox}>
          <summary style={summaryHeading}><strong>{summary.connected}</strong><span>Connected</span></summary>
          <div style={summaryList}>{summary.connectedItems.length ? summary.connectedItems.map((item) => <button key={item.name} type="button" onClick={() => openConnectionDetails(item.name)} style={summaryItemButton}><strong>{displayNameFor(item.name)} — {statusLabel(item.status)}</strong><small>{connectionInsight(item, item.name)}</small><span>View connection details →</span></button>) : <span>Nothing connected yet.</span>}</div>
        </details>
        <details style={summaryBox}>
          <summary style={summaryHeading}><strong>{summary.setup}</strong><span>In setup</span></summary>
          <div style={summaryList}>{summary.setupItems.length ? summary.setupItems.map((item) => <button key={item.name} type="button" onClick={() => openConnectionDetails(item.name)} style={summaryItemButton}><strong>{displayNameFor(item.name)} — {statusLabel(item.status)}</strong><small>{connectionInsight(item, item.name)}</small><span>Continue setup →</span></button>) : <span>No setup tasks in progress.</span>}</div>
        </details>
        <details style={summaryBox}>
          <summary style={summaryHeading}><strong>{summary.needsKeys}</strong><span>Needs keys</span></summary>
          <div style={summaryList}>{summary.needsKeyItems.length ? summary.needsKeyItems.map((item) => <button key={item.name} type="button" onClick={() => openConnectionDetails(item.name)} style={summaryItemButton}><strong>{displayNameFor(item.name)} — needs provider credentials or approval</strong><small>{connectionInsight(item, item.name)}</small><span>See what is needed →</span></button>) : <span>No credential blockers recorded.</span>}</div>
        </details>
        <div style={summaryBox}><strong>{plan}</strong><span>Current access</span></div>
      </div>

      {msg && <div style={notice}>{msg}</div>}

      <MetaAnalyticsConnection workspaceId={activeWorkspace?.id} />

      <section style={guidePanel}>
        <div style={guideMain}>
          <div style={eyebrowDark}>No-roadblock connection guide</div>
          <div style={assistTitle}>If provider approval is pending, OmniMark keeps moving</div>
          <p style={assistCopy}>
            Some providers block OAuth until their app settings are approved. That is normal. Copy the callback URL, save the setup status, and use manual notes until the provider app is ready.
          </p>
          <div style={pathGrid}>
            {[
              ["1", "Pick connector", "Choose Meta, Shopify, TikTok, Stripe, suppliers, or your domain."],
              ["2", "Check secure requirements", "Configure the approved provider app and OmniMark server exchange before starting OAuth."],
              ["3", "Use rescue path", "If blocked, copy the callback URL and save the provider task without losing work."],
              ["4", "AI uses what exists", "Connected data, manual notes, copied URLs, and uploaded files can all power the workflow."],
            ].map(([number, label, copy]) => (
              <div key={label} style={pathCard}>
                <span style={pathNumber}>{number}</span>
                <strong>{label}</strong>
                <small>{copy}</small>
              </div>
            ))}
          </div>
        </div>
        <div style={rescueCard}>
          <div style={rescueBadge}>URL blocked fix</div>
          <strong>Meta says the redirect URI is not whitelisted?</strong>
          <span>Add this exact URL in the Meta app OAuth settings:</span>
          <code style={codeBox}>{metaCallbackUrl}</code>
          <div style={buttonRow}>
            <button type="button" onClick={copyCallbackUrl} style={darkButton}>Copy Meta API callback</button>
            <button type="button" onClick={() => startProviderSignIn("Meta Marketing API")} style={outlineButton}>Connect Meta + Instagram</button>
            <button type="button" onClick={() => saveBlockedRedirectNote("Meta Marketing API")} style={outlineButton}>Save as needs setup</button>
          </div>
        </div>
      </section>

      <section style={providerSetupPanel}>
        <div>
          <div style={eyebrowDark}>Owner setup center</div>
          <div style={assistTitle}>Turn on the same one-click flow for each platform</div>
          <p style={assistCopy}>
            Customers will never paste tokens. You create or select OmniMark&apos;s approved developer app once; then OmniMark can send each customer to that platform&apos;s sign-in and consent page, receive the return securely, and test the connection before enabling any publishing.
          </p>
        </div>
        <div style={providerSetupGrid}>
          {providerSetupPortals.map((provider) => (
            <article key={provider.name} style={providerSetupCard}>
              <strong>{provider.label}</strong>
              <span>{provider.need}</span>
              <div style={buttonRow}>
                <a
                  href={provider.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => startSetup(provider.name)}
                  style={outlineButton}
                >
                  Open official developer portal
                </a>
                <button type="button" onClick={() => startSetup(provider.name)} style={item}>Save setup task</button>
              </div>
            </article>
          ))}
        </div>
        <div style={miniNotice}>
          Meta + Instagram is already the first real one-click handoff. The remaining buttons become customer sign-ins only after their respective developer app, approved scopes, API callback, encrypted token exchange, and provider test are complete.
        </div>
      </section>

      <section style={assistPanel}>
        <div>
          <div style={eyebrowDark}>AI setup assistant</div>
          <div style={assistTitle}>{setupTitle}</div>
          <p style={assistCopy}>
            {setupCopy}
          </p>
        </div>
        <div style={assistForm}>
          <label style={fieldWrap}>
            <span>Business or page name</span>
            <input value={assistBusiness} onChange={(e) => setAssistBusiness(e.target.value)} style={input} />
          </label>
          <label style={fieldWrap}>
            <span>Website/domain</span>
            <input value={assistWebsite} onChange={(e) => setAssistWebsite(e.target.value)} style={input} />
          </label>
          <label style={fieldWrap}>
            <span>Meta/Facebook page</span>
            <input value={assistMetaPage} onChange={(e) => setAssistMetaPage(e.target.value)} style={input} />
          </label>
          <div style={buttonRow}>
            <button type="button" onClick={startMyValueOfLifeMeta} style={darkButton}>Prepare Meta setup</button>
            <button type="button" onClick={startMyValueOfLifeSite} style={outlineButton}>Prepare website setup</button>
            <button type="button" onClick={copyMyValueOfLifeChecklist} style={outlineButton}>Copy checklist</button>
            {canUseMyValueShortcut && !isMyValueWorkspace && (
              <button
                type="button"
                onClick={() => {
                  setAssistBusiness("My Value of Life");
                  setAssistWebsite("https://myvalueoflife.com");
                  setAssistMetaPage("My Value of Life");
                  setMsg("My Value of Life setup helper loaded for your admin account.");
                }}
                style={outlineButton}
              >
                Load My Value setup
              </button>
            )}
          </div>
        </div>
        <div style={assistSteps}>
          {[
            "Confirm Page access in Meta Business Suite.",
            metaStep,
            "Save Ad Account ID, App ID, token/OAuth status.",
            "Add website/domain and pixel/dataset when ready.",
          ].map((step) => <div key={step} style={assistStep}>{step}</div>)}
        </div>
      </section>

      <section style={layoutGrid}>
        <div style={panel}>
          <div style={sectionTitle}>Best Next Connectors</div>
          <p style={muted}>Build these in this order so OmniMark can move from product discovery to supplier sourcing to live ad optimization.</p>
          <div style={priorityGrid}>
            {priorityNames.map((name) => {
              const connector = connectorDetails[name];
              const record = records[name] || defaultConnection(name);
              return (
                <div key={name} style={priorityCard}>
                  <div style={priorityTop}>
                    <span style={orderBadge}>{connector.order}</span>
                    <div>
                      <strong>{connector.label || name}</strong>
                      <div style={mutedTight}>{connector.status}</div>
                    </div>
                    <span style={statusChip(record.status)}>{statusLabel(record.status)}</span>
                  </div>
                  <div style={priorityDetail}>{connector.detail}</div>
                  <div style={record.status === "verified" ? connectedCallout : softCallout}>
                    {connectionInsight(record, name)}
                  </div>
                  <div style={tagWrap}>
                    {connector.unlocks.map((unlock) => <span key={unlock} style={tag}>{unlock}</span>)}
                  </div>
                  <div style={buttonRow}>
                    <button type="button" onClick={() => startSetup(name)} style={item}>Setup</button>
                    <button type="button" onClick={() => startSetup(name)} style={item}>View requirements</button>
                    <button type="button" onClick={() => markReadyForVerification(name)} style={itemActive}>Ready to verify</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside id="provider-setup" ref={setupPanelRef} tabIndex={-1} aria-label={`${selectedName} setup`} style={panel}>
          <div style={sectionTitle}>{selectedName} Setup</div>
          {selectedIsShopify && shopifyReturnResult && msg && (
            <div role={shopifyReturnResult.startsWith("failed") || shopifyReturnResult === "storage_failed" ? "alert" : "status"} style={notice}>{msg}</div>
          )}
          <p style={muted}>{selectedDetails.detail}</p>
          <div style={selected.status === "verified" ? connectedStatusPanel : statusPanel}>
            <strong>{statusLabel(selected.status)}</strong>
            <span>{connectionInsight(selected, selectedName)}</span>
            {selected.oauth?.returned_at && <small>Last OAuth return: {new Date(selected.oauth.returned_at).toLocaleString()}</small>}
          </div>
          <div style={selectedHelpBox}>
            <div style={priorityTop}>
              <span style={orderBadge}>?</span>
              <div>
                <strong>{selectedHelp.title}</strong>
                <div style={mutedTight}>{selectedHelp.fallback}</div>
              </div>
            </div>
            <div style={helpSteps}>
              {selectedHelp.steps.map((step) => <div key={step} style={helpStep}>{step}</div>)}
            </div>
            <div style={buttonRow}>
              <button type="button" onClick={copyProviderFixSteps} style={outlineButton}>Copy help steps</button>
              <button type="button" onClick={saveBlockedRedirectNote} style={outlineButton}>Save provider task</button>
            </div>
          </div>
          <div style={setupFields}>
            {(selectedDetails.fields || []).map((field) => (
              <label key={field} style={fieldWrap}>
                <span>{field}</span>
                {isSensitiveConnectionField(field) ? (
                  <div style={miniNotice}>Sensitive credentials are never entered or stored in this browser. Add them only to OmniMark’s protected server configuration after the provider app is approved.</div>
                ) : (
                  <input
                    value={selected.credentials?.[field] || ""}
                    onChange={(e) => updateCredential(field, e.target.value)}
                    placeholder={`Enter ${field.toLowerCase()}`}
                    style={input}
                  />
                )}
              </label>
            ))}
            <label style={fieldWrap}>
              <span>Setup notes</span>
              <textarea
                value={selected.notes || ""}
                onChange={(e) => updateNotes(e.target.value)}
                placeholder="OAuth app status, scopes, supplier region, ad account notes, or owner task."
                style={textarea}
              />
            </label>
          </div>
          <div style={buttonRow}>
            <button
              type="button"
              onClick={() => {
                if (selectedHasSecureOAuth) {
                  startProviderSignIn(selectedName);
                  return;
                }
                setMsg(`${selectedProviderLabel} does not have a configured secure OAuth bridge yet. No provider sign-in or redirect is available until its developer app and server-side exchange are installed.`);
              }}
              style={darkButton}
            >
              {selectedHasSecureOAuth ? `Connect ${selectedProviderLabel} securely` : "View secure setup requirements"}
            </button>
            <button type="button" onClick={() => markReadyForVerification(selectedName)} style={darkButton}>Ready to verify</button>
            <button type="button" onClick={() => markNeedsKeys(selectedName)} style={outlineButton}>Needs keys</button>
            <button type="button" onClick={testConnection} style={outlineButton}>{testing ? "Checking..." : "Check OmniMark API"}</button>
            {selectedIsMeta && <button type="button" onClick={() => copyCallbackUrl("Meta")} style={outlineButton}>Copy Meta API callback</button>}
            {selectedIsTikTok && <button type="button" onClick={() => copyCallbackUrl("TikTok")} style={outlineButton}>Copy TikTok API callback</button>}
            {selectedIsGoogle && <button type="button" onClick={() => copyCallbackUrl("Google")} style={outlineButton}>Copy Google API callback</button>}
            {selectedIsShopify && <button type="button" onClick={() => copyCallbackUrl("Shopify")} style={outlineButton}>Copy Shopify API callback</button>}
            {selectedProviderPortal && (
              <a href={selectedProviderPortal.url} target="_blank" rel="noreferrer" onClick={() => startSetup(selectedName)} style={outlineButton}>
                Open official developer portal
              </a>
            )}
            <button type="button" onClick={copySetupBrief} style={outlineButton}>Copy setup brief</button>
            <Link to={selectedDetails.actionRoute || groupRouteFor(selectedName)} style={outlineButton}>Open tool</Link>
          </div>
          {selected.last_test && <div style={miniNotice}>{selected.last_test}</div>}
        </aside>
      </section>

      <section style={groupGrid}>
        {groups.map((group) => (
          <div key={group.title} style={panel}>
            <div style={sectionHeader}>
              <div>
                <div style={sectionTitle}>{group.title}</div>
                <p style={muted}>{group.desc}</p>
              </div>
              <Link to={group.route} style={outlineButton}>Open</Link>
            </div>
            <div style={itemGrid}>
              {group.items.map((name) => {
                const connectorName = normalizeOauthName(name);
                const record = records[connectorName] || defaultConnection(connectorName);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => startSetup(connectorName)}
                    style={record.status === "verified" ? connectorActive : connectorItem}
                  >
                    <span>{displayNameFor(name)}</span>
                    <small>{statusLabel(record.status)} - {record.oauth?.code_received ? "OAuth returned" : "setup path"}</small>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <section style={panel}>
        <div style={sectionTitle}>What OmniMark AI can do with verified connections</div>
        <div style={automationGrid}>
          {[
            "Use verified product and domain context before building pages.",
            "Create product pages, ad creatives, and audience tests from a copied URL.",
            "Recommend pausing ads when spend is wasting; live actions require a verified provider connection and approval.",
            "Prepare notifications when ads need new videos, hooks, or budget changes; delivery requires a verified provider connection.",
            "Use customer chat and store events to improve offers and FAQs.",
          ].map((x) => (
            <div key={x} style={automationItem}>{x}</div>
          ))}
        </div>
      </section>
    </div>
  );
}

const page = { maxWidth: 1180, margin: "0 auto", display: "grid", gap: 16 };
const hero = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, borderRadius: 8, padding: 24, background: "linear-gradient(135deg,#07121f,#12392f 58%,#3b2f75)", color: "#fff", flexWrap: "wrap", boxShadow: "0 18px 55px rgba(15,23,42,.16)" };
const heroActions = { display: "flex", flexWrap: "wrap", gap: 8 };
const eyebrow = { color: "#9ee7d4", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const eyebrowDark = { color: "#0f766e", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const title = { margin: "6px 0", fontSize: 34, lineHeight: 1.05 };
const subtitle = { margin: 0, color: "rgba(255,255,255,.75)", maxWidth: 760 };
const lightButton = { background: "#fff", color: "#101828", textDecoration: "none", borderRadius: 8, padding: "10px 12px", fontWeight: 900, whiteSpace: "nowrap" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 };
const summaryBox = { border: "1px solid rgba(0,0,0,.1)", background: "#fff", borderRadius: 8, padding: 14, display: "grid", gap: 8 };
const summaryHeading = { display: "grid", gap: 4, cursor: "pointer", listStyle: "none" };
const summaryList = { display: "grid", gap: 6, borderTop: "1px solid #eaecf0", paddingTop: 8, color: "#475467", fontSize: 12, lineHeight: 1.35 };
const summaryItemButton = { appearance: "none", width: "100%", border: "1px solid #eaecf0", borderRadius: 8, background: "#f8fafc", color: "#344054", cursor: "pointer", display: "grid", gap: 4, padding: "9px 10px", textAlign: "left", font: "inherit" };
const notice = { border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1e3a8a", borderRadius: 8, padding: 12 };
const miniNotice = { border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", borderRadius: 8, padding: 10, marginTop: 10, fontSize: 13 };
const guidePanel = { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(280px,.45fr)", gap: 16, border: "1px solid rgba(15,23,42,.1)", background: "#fff", borderRadius: 8, padding: 16 };
const guideMain = { display: "grid", gap: 10 };
const pathGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 };
const pathCard = { display: "grid", alignContent: "start", gap: 6, border: "1px solid rgba(0,0,0,.08)", background: "#f8fafc", borderRadius: 8, padding: 12 };
const pathNumber = { width: 28, height: 28, borderRadius: 8, display: "grid", placeItems: "center", background: "#111827", color: "#fff", fontWeight: 900 };
const rescueCard = { display: "grid", alignContent: "start", gap: 10, border: "1px solid #fed7aa", background: "#fff7ed", borderRadius: 8, padding: 14 };
const rescueBadge = { justifySelf: "start", borderRadius: 999, background: "#ffedd5", color: "#9a3412", padding: "4px 8px", fontSize: 12, fontWeight: 900 };
const codeBox = { display: "block", whiteSpace: "normal", overflowWrap: "anywhere", border: "1px solid rgba(0,0,0,.1)", background: "#fff", borderRadius: 8, padding: 10, fontSize: 12, color: "#111827" };
const providerSetupPanel = { display: "grid", gap: 14, border: "1px solid #c7d2fe", background: "linear-gradient(135deg,#eef2ff,#f8fafc)", borderRadius: 8, padding: 16 };
const providerSetupGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 };
const providerSetupCard = { display: "grid", alignContent: "start", gap: 8, border: "1px solid rgba(30,64,175,.16)", background: "#fff", borderRadius: 8, padding: 12, color: "#344054", fontSize: 13, lineHeight: 1.4 };
const assistPanel = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14, border: "1px solid #bbf7d0", background: "linear-gradient(135deg,#f0fdf4,#eff6ff)", borderRadius: 8, padding: 16 };
const assistTitle = { fontSize: 22, fontWeight: 900, marginTop: 4 };
const assistCopy = { color: "#344054", fontSize: 13, lineHeight: 1.45, margin: "8px 0 0" };
const assistForm = { display: "grid", gap: 8 };
const assistSteps = { display: "grid", gap: 8 };
const assistStep = { border: "1px solid rgba(0,0,0,.08)", borderRadius: 8, background: "rgba(255,255,255,.72)", padding: 10, fontSize: 13, color: "#344054" };
const layoutGrid = { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(320px,.48fr)", gap: 16 };
const priorityGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 };
const priorityCard = { display: "grid", gap: 10, border: "1px solid rgba(0,0,0,.1)", background: "#fbfcff", borderRadius: 8, padding: 12 };
const priorityTop = { display: "grid", gridTemplateColumns: "34px 1fr auto", gap: 10, alignItems: "center" };
const orderBadge = { width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", background: "#101828", color: "#fff", fontWeight: 900 };
const priorityDetail = { color: "#344054", fontSize: 13, lineHeight: 1.4 };
const softCallout = { border: "1px solid #e5e7eb", background: "#f8fafc", color: "#344054", borderRadius: 8, padding: 10, fontSize: 12, lineHeight: 1.35 };
const connectedCallout = { ...softCallout, borderColor: "#86efac", background: "#f0fdf4", color: "#166534", fontWeight: 800 };
const mutedTight = { color: "#667085", fontSize: 13 };
const tagWrap = { display: "flex", flexWrap: "wrap", gap: 6 };
const tag = { borderRadius: 999, background: "#eef6ff", padding: "3px 8px", fontSize: 12, fontWeight: 800 };
const groupGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 };
const panel = { border: "1px solid rgba(0,0,0,.1)", background: "#fff", borderRadius: 8, padding: 16, minWidth: 0 };
const statusPanel = { display: "grid", gap: 4, border: "1px solid #e5e7eb", background: "#f8fafc", color: "#344054", borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 };
const connectedStatusPanel = { ...statusPanel, borderColor: "#86efac", background: "#f0fdf4", color: "#166534" };
const sectionHeader = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 };
const sectionTitle = { fontSize: 18, fontWeight: 900, marginBottom: 8 };
const muted = { color: "#667085", margin: "0 0 12px", fontSize: 13, lineHeight: 1.4 };
const itemGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(132px,1fr))", gap: 8 };
const connectorItem = { display: "grid", gap: 3, textAlign: "left", border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: "10px 12px", fontWeight: 800, cursor: "pointer" };
const connectorActive = { ...connectorItem, background: "#ecfdf5", borderColor: "#34d399" };
const item = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, background: "#fff", padding: "8px 10px", fontWeight: 900, cursor: "pointer", textDecoration: "none", color: "#111827" };
const itemActive = { ...item, background: "#ecfdf5", borderColor: "#34d399" };
const darkButton = { border: "none", borderRadius: 8, background: "#111827", color: "#fff", padding: "10px 12px", fontWeight: 900, cursor: "pointer" };
const connectionDirectory = { display: "grid", gap: 10, width: "100%", maxWidth: 560, border: "1px solid rgba(0,0,0,.12)", borderRadius: 10, padding: 12, background: "rgba(255,255,255,.78)", color: "#344054", fontSize: 13, lineHeight: 1.4 };
const directoryActions = { display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8 };
const directorySelect = { minWidth: 0, width: "100%", border: "1px solid rgba(0,0,0,.18)", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", background: "#fff", color: "#111827" };
const outlineButton = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, background: "#fff", color: "#111827", padding: "10px 12px", fontWeight: 900, cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap" };
const buttonRow = { display: "flex", flexWrap: "wrap", gap: 8 };
const setupFields = { display: "grid", gap: 10 };
const selectedHelpBox = { display: "grid", gap: 10, border: "1px solid #dbeafe", background: "#eff6ff", borderRadius: 8, padding: 12, marginBottom: 12 };
const helpSteps = { display: "grid", gap: 6 };
const helpStep = { border: "1px solid rgba(30,64,175,.12)", background: "#fff", borderRadius: 8, padding: 8, color: "#1e3a8a", fontSize: 12, lineHeight: 1.35 };
const fieldWrap = { display: "grid", gap: 6, fontSize: 13, fontWeight: 800 };
const input = { width: "100%", border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit" };
const textarea = { ...input, minHeight: 110, resize: "vertical" };
const automationGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 };
const automationItem = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, padding: 12, background: "#f8fafc", fontSize: 14 };

function statusChip(status) {
  const base = { borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" };
  if (status === "verified") return { ...base, background: "#dcfce7", color: "#166534" };
  if (status === "oauth_returned" || status === "verification_required") return { ...base, background: "#fef3c7", color: "#92400e" };
  if (status === "setup") return { ...base, background: "#dbeafe", color: "#1e3a8a" };
  if (status === "needs_keys") return { ...base, background: "#fef3c7", color: "#92400e" };
  return { ...base, background: "#f2f4f7", color: "#475467" };
}

function connectionHelpFor(name, callbackUrl) {
  const connector = normalizeOauthName(name);
  const callbackStep = callbackUrl
    ? `Register this exact OmniMark API callback: ${callbackUrl}`
    : "Do not register the old browser callback. OmniMark will provide this provider's API callback when its secure server-side OAuth flow is enabled.";
  if (connector === "Meta Marketing API") {
    return {
      title: "Meta + Instagram setup",
      steps: [
        "Open developers.facebook.com and choose the Meta app that owns this App ID.",
        "Go to Facebook Login > Settings.",
        "Turn on Client OAuth Login and Web OAuth Login.",
        `Add this exact Valid OAuth Redirect URI: ${callbackUrl}`,
        "First sign-in connects Meta/Facebook ads, pages, and business permissions without asking for Instagram scopes.",
        "Inside the same OmniMark connection, save Instagram business account ID and review status so Instagram can turn on after Meta approves those permissions.",
        "Save changes, return to OmniMark, then press Sign in again if the redirect was blocked.",
      ],
      fallback: "This is one combined Meta + Instagram connection. Instagram is staged inside it so a missing Instagram approval does not break the Meta/Facebook sign-in.",
    };
  }
  if (connector === "Shopify") {
    return {
      title: "Shopify read-only store connection",
      steps: [
        "Paste the Shopify store URL, such as mystore.myshopify.com.",
        callbackStep,
        "In the OmniMark Shopify app version, use the exact callback above and request only read_products and read_inventory.",
        "Release that app version only after its protected server client ID and client secret are installed in OmniMark.",
        "Press Connect Shopify securely. The merchant signs in at Shopify and returns here after OmniMark validates the store.",
      ],
      fallback: "This connection is read-only. OmniMark does not request order, customer, product-write, or storefront-admin permissions.",
    };
  }
  if (connector === "TikTok Business API") {
    return {
      title: "TikTok Login Kit setup",
      steps: [
        "In TikTok for Developers, add Login Kit to the OmniMark app and enter the exact callback below.",
        callbackStep,
        "Add the TikTok client key and client secret only to OmniMark's protected server configuration; never paste them into this page.",
        "Record the resulting real sign-in → consent → OmniMark return flow for TikTok's app-review demo.",
        "Request Content Posting separately. OmniMark does not post until TikTok approves it and the publishing worker is enabled.",
      ],
      fallback: "Login Kit may connect an account once configured. Posting and reporting stay unavailable until their specific TikTok products and scopes are approved.",
    };
  }
  if (connector === "Google Ads") {
    return {
      title: "Google OAuth setup path",
      steps: [
        "Add the Google OAuth client ID.",
        callbackStep,
        "Use the Google Ads customer ID from the account switcher.",
        "Return and sign in after the consent screen is configured.",
      ],
      fallback: "Manual customer IDs and exported reports can be saved while OAuth consent is pending.",
    };
  }
  if (connector === "YouTube") {
    return {
      title: "YouTube private-upload setup",
      steps: [
        "Create a Google OAuth web client and enable YouTube Data API in the same Google Cloud project.",
        callbackStep,
        "Connect with read-only and YouTube upload permission; OmniMark uses upload access only after an admin approves a specific request.",
        "After the provider returns here, OmniMark exchanges the authorization code on the server and verifies the channel before calling it connected.",
      ],
      fallback: "The connection verifies the channel and unlocks an approval-gated Private upload queue. Public posting and scheduling remain unavailable.",
    };
  }
  if (connector === "LinkedIn") {
    return {
      title: "LinkedIn organization setup",
      steps: [
        "Create or select the LinkedIn developer application owned by the organization administrator.",
        callbackStep,
        "Start with member publishing consent; request Advertising API access separately before promising ad metrics.",
        "Save the organization ID and product-approval state so OmniAssist can tell the customer exactly what remains.",
      ],
      fallback: "Organization posts can be staged as drafts while marketing API approval is pending; OmniMark will not claim live ad data until approved.",
    };
  }
  if (connector === "Pinterest") {
    return {
      title: "Pinterest business setup",
      steps: [
        "Create or select a Pinterest developer app for the business account.",
        callbackStep,
        "Begin with board and Pin scopes; add ad scopes only when the customer enables reporting or ad management.",
        "After the return, OmniMark must exchange the code and confirm the business account before marking it live.",
      ],
      fallback: "Use the Pin planner and draft exports until Pinterest token exchange and account validation are complete.",
    };
  }
  if (connector === "X Ads") {
    return {
      title: "X setup path",
      steps: [
        "Create an X developer app and enable user-context OAuth with the minimum read/write permissions required.",
        callbackStep,
        "Use the provider's required PKCE authorization-code flow; do not put app secrets in the browser.",
        "Save the Ads account ID and app permission status, then let OmniMark run a server-side connection test.",
      ],
      fallback: "Keep X content in an approval queue until server-side PKCE token exchange and account validation are enabled.",
    };
  }
  return {
    title: `${connector} setup guide`,
    steps: [
      "Start setup and save the account URL, API key, owner notes, or approval status.",
      "Use Copy setup brief to send exact requirements to the account owner.",
      "Mark Needs keys if the outside account is not ready yet.",
      "Open the related OmniMark tool to keep building with copied links, notes, or uploads.",
    ],
    fallback: "This connector can be tracked manually until the provider publishes or approves official API access.",
  };
}
